import { useState } from 'react'
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Segmented,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PlusOutlined, WarningOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { PolicyFormModal } from '@/features/catalog/PolicyFormModal'
import {
  archivePolicy,
  DELIVERY_METHODS,
  fetchPolicies,
  fetchPolicyAttributes,
  type DeliveryMethod,
  type Policy,
} from '@/features/catalog/catalogApi'
import {
  timeWindowDetails,
  timeWindowRange,
  type TimeWindowKind,
} from '@/features/catalog/timeWindows'
import { Can } from '@/shared/auth/Can'
import { formatBytes, formatDuration } from '@/shared/lib/format'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { QueryErrorState } from '@/shared/ui/QueryErrorState'
import { RowActions } from '@/shared/ui/table/RowActions'
import { displayName, hasTranslation } from '@/shared/lib/planName'

const WINDOW_COLORS: Record<TimeWindowKind, string> = {
  FREE: 'green',
  SPEED: 'blue',
  ACCESS: 'orange',
}

/**
 * Siyosatlar (Policy) — TEXNIK cheklov: tezlik, timeout, RADIUS atributlari.
 *
 * Paketlardan (Plan) alohida sahifada: bitta siyosat bir necha paketda
 * ishlatiladi va uni "paket tahriri" ichida o'zgartirish boshqa paketlarga
 * ham jimgina ta'sir qilardi.
 */
export function PoliciesPage() {
  const { t, i18n } = useTranslation()
  // ⚠️ BUG-048 — `modal` KONTEKSTDAN: statik `Modal.confirm` React
  // daraxtidan tashqarida yangi ildiz ochadi va `ConfigProvider`
  // mavzusiga yetmaydi — qorong'i panel ustida OQ dialog chiziladi.
  const { modal } = App.useApp()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Policy | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [inspecting, setInspecting] = useState<Policy | null>(null)
  const [error, setError] = useState<string | null>(null)
  /*
    ⚠️ Sahifa BOSHQARILADI: `render` ga keladigan `index` joriy sahifadagi
    o'rin, ya'ni boshqarilmagan holatda 2-sahifa yana 1 dan boshlanardi.
  */
  const [paging, setPaging] = useState({ current: 1, pageSize: 25 })

  /**
   * Qaysi yetkazish usuli uchun atributlar ko'rsatilyapti.
   *
   * Siyosatning O'ZIDA bunday maydon yo'q — u paket (Plan) darajasida
   * beriladi, bitta siyosat esa turli usuldagi paketlarda ishlatilishi
   * mumkin. Shuning uchun tanlov shu yerda va PPPoE — eng ko'p uchraydigan
   * holat sifatida boshlang'ich qiymat.
   */
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('PPPOE')

  const policies = useQuery({ queryKey: queryKeys.catalog.policies, queryFn: fetchPolicies })

  // RADIUS atributlari faqat panel ochilganda so'raladi — ular har qator
  // uchun alohida chaqiruv va ro'yxatda kerak emas
  const attributes = useQuery({
    queryKey: queryKeys.catalog.policyAttributes(inspecting?.id ?? '', deliveryMethod),
    queryFn: () => fetchPolicyAttributes(inspecting?.id ?? '', deliveryMethod),
    enabled: inspecting !== null,
  })

  const archive = useMutation({
    mutationFn: (id: string) => archivePolicy(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.catalog.policies }),
    onError: (err) => setError(translateError(err, t)),
  })

  const columns: ColumnsType<Policy> = [
    {
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => (
        <Typography.Text type="secondary">
          {(paging.current - 1) * paging.pageSize + index + 1}
        </Typography.Text>
      ),
    },
    {
      key: 'name',
      title: t('catalog.policy.name'),
      width: 220,
      // ⚠️ Tarjimasiz siyosat — QIZIL EMAS. Sabab PlansPage dagi izohda:
      // tarjimasizlik nosozlik emas, u kanonik nom bilan ishlab turaveradi.
      render: (_, row) => (
        <>
          <Typography.Text strong>{displayName(row, i18n.language)}</Typography.Text>
          {hasTranslation(row, i18n.language) ? null : (
            <Tooltip title={t('catalog.policy.untranslatedHint')}>
              <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                {t('catalog.policy.untranslated')}
              </Typography.Text>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      key: 'rateLimit',
      title: t('tariffs.rateLimit'),
      width: 160,
      render: (_, row) =>
        row.rateLimit ? <Typography.Text code>{row.rateLimit}</Typography.Text> : '—',
    },
    {
      key: 'quota',
      title: t('catalog.policy.quotaGb'),
      width: 130,
      align: 'right',
      render: (_, row) => (row.quotaBytes === null ? '—' : formatBytes(row.quotaBytes)),
    },
    {
      key: 'dailyQuota',
      title: t('catalog.policy.dailyQuotaGb'),
      width: 130,
      align: 'right',
      render: (_, row) => (row.dailyQuotaBytes === null ? '—' : formatBytes(row.dailyQuotaBytes)),
    },
    {
      key: 'sessionTimeout',
      title: t('tariffs.form.fieldSessionTimeout'),
      width: 180,
      /*
        ⚠️ SHUBHALI QISQA MUDDAT RO'YXATDA HAM BELGILANADI.

        `tarif-7m` siyosatida `2` turgan edi va abonent ulangach ikki
        soniyada uzilardi. Jadvalda u "2 s" deb, qolganlari bilan bir xil
        ko'rinishda turardi — ya'ni nosozlik sababi ekranda OCHIQ turgani
        holda ko'zga tashlanmasdi. Ogohlantirish tegi uni ajratadi.
      */
      render: (_, row) => {
        if (row.sessionTimeout === null) return t('common.dash')
        const short = row.sessionTimeout > 0 && row.sessionTimeout < 300
        return (
          <Space size={4}>
            <span>{formatDuration(row.sessionTimeout)}</span>
            {short ? (
              <Tooltip title={t('catalog.policy.sessionTimeoutTooShort', { seconds: row.sessionTimeout })}>
                <Tag color="warning" icon={<WarningOutlined />}>
                  {t('catalog.policy.tooShortTag')}
                </Tag>
              </Tooltip>
            ) : null}
          </Space>
        )
      },
    },
    {
      key: 'simultaneousUse',
      title: t('terms.simultaneousUseLabel'),
      width: 130,
      align: 'right',
      render: (_, row) => row.simultaneousUse ?? '—',
    },
    {
      key: 'timeWindows',
      title: t('catalog.policy.window.title'),
      width: 260,
      render: (_, row) =>
        row.timeWindows.length === 0 ? (
          t('common.dash')
        ) : (
          <Space size={[0, 4]} wrap>
            {row.timeWindows.map((window, index) => (
              <Tag key={index} color={WINDOW_COLORS[window.kind]}>
                {t(`catalog.policy.window.tag.${window.kind}`)}{' '}
                <bdi dir="ltr">{timeWindowRange(window)}</bdi>
                {timeWindowDetails(t, window).map((detail) => (
                  <span key={detail}>
                    {' · '}
                    <bdi>{detail}</bdi>
                  </span>
                ))}
              </Tag>
            ))}
          </Space>
        ),
    },
    {
      key: 'state',
      title: t('subscribers.columns.status'),
      width: 120,
      render: (_, row) => (
        <Tag color={row.state === 'ACTIVE' ? 'green' : 'default'}>
          {t(`policyState.${row.state}`)}
        </Tag>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        // "RADIUS atributlari" — huquqsiz DIAGNOSTIKA (ilgari ham shunday
        // edi): ro'yxatni ko'ra olgan odam nima yozilishini ham ko'radi.
        <RowActions
          items={[
            {
              key: 'showRadius',
              label: t('catalog.policy.showRadius'),
              onClick: () => setInspecting(row),
            },
            {
              key: 'edit',
              label: t('tariffs.edit'),
              onClick: () => {
                setEditing(row)
                setFormOpen(true)
              },
              permission: 'plan.update',
            },
            {
              key: 'archive',
              label: t('catalog.plan.archive'),
              onClick: () => {
                modal.confirm({
                  title: t('catalog.policy.archiveTitle'),
                  content: t('catalog.policy.archiveConfirm'),
                  okButtonProps: { danger: true },
                  okText: t('catalog.plan.archive'),
                  cancelText: t('common.cancel'),
                  onOk: () => archive.mutateAsync(row.id),
                })
              },
              permission: 'plan.delete',
              danger: true,
              divider: true,
              hidden: row.state === 'ARCHIVED',
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.policies')}
        description={t('catalog.policy.intro')}
        extra={
          <Can permission="plan.create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditing(null)
                setFormOpen(true)
              }}
            >
              {t('catalog.policy.createTitle')}
            </Button>
          </Can>
        }
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Card>
        {/*
          ⚠️ Xato — bo'sh ro'yxatdan AJRATILGAN holat. Aks holda so'rov
          yiqilganda ekranda «siyosat yo'q» turardi va operator yangi
          siyosat yozishga tushardi — mavjudlari ustiga.
        */}
        {policies.isError ? (
          <QueryErrorState error={policies.error} onRetry={() => void policies.refetch()} />
        ) : (
          <Table<Policy>
            columns={columns}
            dataSource={policies.data ?? []}
            rowKey={(row) => row.id}
            loading={policies.isPending}
            size="middle"
            scroll={{ x: 1560 }}
            pagination={{
              current: paging.current,
              pageSize: paging.pageSize,
              showSizeChanger: true,
            }}
            onChange={(p) =>
              setPaging({ current: p.current ?? 1, pageSize: p.pageSize ?? 25 })
            }
            locale={{
              emptyText: (
                <EmptyState
                  variant="no-data"
                  title={t('catalog.policy.emptyTitle')}
                  description={t('catalog.policy.emptyHint')}
                />
              ),
            }}
          />
        )}
      </Card>

      <PolicyFormModal
        open={formOpen}
        policy={editing}
        onClose={() => setFormOpen(false)}
       
      />

      <Drawer
        open={inspecting !== null}
        title={t('catalog.policy.radiusTitle', {
          name: inspecting === null ? '' : displayName(inspecting, i18n.language),
        })}
        onClose={() => setInspecting(null)}
        width={520}
      >
        <Typography.Paragraph type="secondary">
          {t('catalog.policy.radiusHint')}
        </Typography.Paragraph>

        {/*
          Yetkazish usuli TANLANADI va ekranda ko'rinib turadi: atributlar
          to'plami unga qarab haqiqatan farq qiladi (HOTSPOT'da qo'shimcha
          `Session-Timeout` va `Mikrotik-Total-Limit`). Uni yashirin qilib
          qo'yish "bu siyosat shunday atribut yozadi" degan chala xulosaga
          olib kelardi.
        */}
        <Segmented<DeliveryMethod>
          value={deliveryMethod}
          onChange={setDeliveryMethod}
          options={DELIVERY_METHODS.map((method) => ({
            value: method,
            label: t(`deliveryMethod.${method}`),
          }))}
          style={{ marginBottom: 16 }}
        />

        {attributes.isError ? (
          <Alert
            type="error"
            showIcon
            message={t('dataTable.loadFailed')}
            description={translateError(attributes.error, t)}
          />
        ) : (
          <>
            {/*
              `reply` va `check` ALOHIDA ko'rsatiladi. Bu RADIUS mantiqi:
              `check` — ruxsat berish SHARTLARI (`Simultaneous-Use`,
              `Expiration`), `reply` — javob atributlari (tezlik, timeout).
              Bitta ro'yxatga qo'shish ularning ma'nosini yashirardi.

              Bo'sh ro'yxat ATAYLAB aytiladi: chizilmagan `Descriptions`
              "atribut yo'q" va "so'rov muvaffaqiyatsiz" holatlarini bir xil
              ko'rsatardi — aynan shu nosozlikni yashirgan edi.
            */}
            <Descriptions title={t('subscriber.replyAttributes')} column={1} size="small" bordered>
              {Object.entries(attributes.data?.reply ?? {}).map(([key, value]) => (
                <Descriptions.Item key={key} label={key}>
                  <Typography.Text code>{value}</Typography.Text>
                </Descriptions.Item>
              ))}
            </Descriptions>
            {attributes.data && Object.keys(attributes.data.reply).length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('catalog.policy.noAttributes')} />
            ) : null}

            <div style={{ height: 16 }} />

            <Descriptions title={t('subscriber.checkAttributes')} column={1} size="small" bordered>
              {Object.entries(attributes.data?.check ?? {}).map(([key, value]) => (
                <Descriptions.Item key={key} label={key}>
                  <Typography.Text code>{value}</Typography.Text>
                </Descriptions.Item>
              ))}
            </Descriptions>
            {attributes.data && Object.keys(attributes.data.check).length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('catalog.policy.noAttributes')} />
            ) : null}
          </>
        )}
      </Drawer>
    </>
  )
}
